// components/SourceDisplays.jsx - Composants d'affichage pour SourceManager
import { Button, Chip } from '@mui/material';
import {
  ChevronRight,
  Home,
  Lightbulb,
  Navigation,
  FileEdit,
  MapPin,
  Languages,
  AlertTriangle,
  RefreshCw,
  Globe
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Styles harmonisés
export const harmonizedStyles = `
  .page-title {
    font-size: 1.75rem;
    line-height: 2.25rem;
    font-weight: 700;
  }
  
  .section-title {
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: 600;
  }
  
  .main-text {
    font-size: 1.125rem;
    line-height: 1.75rem;
  }
  
  .objective-text {
    font-size: 1.125rem;
    line-height: 1.75rem;
  }
  
  textarea.input-text, input.input-text {
    font-size: 1.125rem !important;
  }
  
  .button-text {
    font-size: 1rem;
  }

  .geo-analysis-card {
    background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
    border: 1px solid #0ea5e9;
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin: 1.5rem 0;
    box-shadow: 0 4px 6px rgba(14, 165, 233, 0.1);
  }

  .geo-badge {
    background: linear-gradient(135deg, #0ea5e9, #0284c7);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0.25rem;
  }

  .language-badge {
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
    color: white;
    padding: 0.375rem 0.75rem;
    border-radius: 0.75rem;
    font-size: 0.75rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    margin: 0.125rem;
  }

  .auto-config-alert {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    border: 1px solid #22c55e;
    border-radius: 0.75rem;
    padding: 1rem;
    margin: 1rem 0;
    animation: slideIn 0.5s ease-out;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .config-update-item {
    background: rgba(255, 255, 255, 0.6);
    border-radius: 0.5rem;
    padding: 0.75rem;
    margin: 0.5rem 0;
    border-left: 4px solid #22c55e;
  }

  .breadcrumb-section {
    transition: all 0.3s ease;
  }

  .breadcrumb-item {
    transition: all 0.2s ease-in-out;
    cursor: pointer;
  }

  .breadcrumb-item:hover {
    transform: translateY(-1px);
  }

  .breadcrumb-divider {
    color: #9ca3af;
    transition: color 0.2s ease;
  }

  .process-step {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    transition: all 0.3s ease;
    font-weight: 500;
  }

  .process-step.completed {
    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
    color: #1e40af;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  }

  .process-step.active {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    color: #166534;
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
    font-weight: 600;
  }

  .process-step.pending {
    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
    color: #1e40af;
  }

  .process-step:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .step-icon {
    width: 1.25rem;
    height: 1.25rem;
    margin-right: 0.5rem;
  }

  .step-connector {
    width: 2rem;
    height: 2px;
    background: linear-gradient(90deg, #e5e7eb, #d1d5db);
    margin: 0 0.5rem;
  }

  .step-connector.completed {
    background: linear-gradient(90deg, #22c55e, #16a34a);
  }

  .recovery-card {
    background: linear-gradient(135deg, #fff7ed, #fed7aa);
    border: 1px solid #fb923c;
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin: 1.5rem 0;
    box-shadow: 0 4px 6px rgba(251, 146, 60, 0.1);
  }

  .accessibility-card {
    background: linear-gradient(135deg, #eff6ff, #dbeafe);
    border: 1px solid #3b82f6;
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin: 1.5rem 0;
    box-shadow: 0 4px 6px rgba(59, 130, 246, 0.1);
  }

  .stat-box {
    background: rgba(255, 255, 255, 0.6);
    border-radius: 0.5rem;
    padding: 0.75rem;
    text-align: center;
    backdrop-filter: blur(10px);
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
  }

  .stat-label {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }

  .fallback-detail {
    background: rgba(255, 255, 255, 0.4);
    border-radius: 0.375rem;
    padding: 0.5rem;
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .url-text {
    font-family: 'Courier New', monospace;
    word-break: break-all;
    font-size: 0.75rem;
  }
`;

// Style pour les onglets
export const tabStyle = {
  minWidth: '50%',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.9rem',
};

/**
 * Composant d'affichage de l'analyse géographique
 */
export const GeographicAnalysisDisplay = ({ analysis, autoConfigApplied, onApplyConfig }) => {
  if (!analysis || !analysis.hasGeographicContext) {
    return null;
  }

  return (
    <div className="geo-analysis-card">
      <h3 className="text-blue-800 font-semibold mb-3 flex items-center section-title">
        <MapPin className="h-6 w-6 mr-2" />
        Analyse géographique automatique
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
        <div>
          <p className="text-blue-700 text-sm font-medium mb-2">Pays détectés :</p>
          <div className="flex flex-wrap gap-2">
            {analysis.countries.map((country, index) => (
              <div key={index} className="geo-badge">
                <MapPin className="h-4 w-4" />
                <span>{country.name} ({country.code})</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-blue-700 text-sm font-medium mb-2">Extensions prioritaires :</p>
          <div className="flex flex-wrap gap-1">
            {analysis.countries[0]?.domains?.slice(0, 4).map((domain, index) => (
              <Chip 
                key={index} 
                label={domain} 
                size="small" 
                sx={{ 
                  backgroundColor: '#e0f2fe', 
                  color: '#0369a1',
                  fontSize: '0.75rem' 
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {autoConfigApplied && (
        <div className="auto-config-alert">
          <div className="flex items-center mb-2">
            <div className="bg-green-500 rounded-full p-1 mr-2">
              <ChevronRight className="h-4 w-4 text-white transform rotate-90" />
            </div>
            <span className="font-semibold text-green-800">Configuration automatique appliquée</span>
          </div>
          
          <div className="space-y-2">
            {autoConfigApplied.country && (
              <div className="config-update-item">
                <span className="font-medium text-green-700">Pays ciblé :</span>
                <span className="ml-2 text-green-800">{autoConfigApplied.country.name} ({autoConfigApplied.country.code})</span>
              </div>
            )}
            
            {autoConfigApplied.languages && autoConfigApplied.languages.length > 0 && (
              <div className="config-update-item">
                <span className="font-medium text-green-700">Langues optimisées :</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {autoConfigApplied.languages.map((lang, index) => (
                    <span key={index} className="language-badge">
                      <Languages className="h-3 w-3" />
                      {lang.toUpperCase()}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!autoConfigApplied && onApplyConfig && (
        <div className="text-center mt-4">
          <Button
            variant="contained"
            startIcon={<MapPin />}
            onClick={onApplyConfig}
            sx={{
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              },
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Appliquer la configuration géographique
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Composant d'affichage de l'analyse sémantique
 */
export const SemanticAnalysisDisplay = ({ semanticAnalysis }) => {
  if (!semanticAnalysis) {
    return null;
  }

  return (
    <div className="mt-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6 shadow-sm">
      <h3 className="text-indigo-800 font-semibold mb-3 flex items-center section-title">
        <Navigation className="h-6 w-6 mr-2" />
        Analyse sémantique active
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="text-indigo-700 text-sm mb-3">
            <span className="font-medium">Intention détectée :</span>
            <span className="ml-2 px-3 py-1 bg-indigo-100 rounded-full text-xs font-medium">
              {semanticAnalysis.intention === 'learn' && 'Apprendre'}
              {semanticAnalysis.intention === 'find' && 'Trouver'}
              {semanticAnalysis.intention === 'compare' && 'Comparer'}
              {semanticAnalysis.intention === 'decide' && 'Décider'}
              {semanticAnalysis.intention === 'solve' && 'Résoudre'}
              {semanticAnalysis.intention === 'explore' && 'Explorer'}
              {semanticAnalysis.intention === 'evaluate' && 'Évaluer'}
              {semanticAnalysis.intention === 'purchase' && 'Acheter'}
            </span>
          </p>
          <p className="text-indigo-700 text-sm">
            <span className="font-medium">Niveau de complexité :</span>
            <span className="ml-2 px-3 py-1 bg-purple-100 rounded-full text-xs font-medium">
              {semanticAnalysis.complexity || 'Intermédiaire'}
            </span>
          </p>
        </div>
        <div>
          <p className="text-indigo-700 text-sm mb-2 font-medium">Mots-clés optimisés :</p>
          <div className="flex flex-wrap gap-1">
            {semanticAnalysis.keyTerms?.slice(0, 8).map((term, index) => (
              <span key={index} className="bg-blue-100 text-blue-800 text-xs rounded-full px-2 py-0.5">
                {term}
              </span>
            ))}
          </div>
          {semanticAnalysis.keyTerms?.length > 8 && (
            <p className="text-xs text-indigo-600 mt-2">
              +{semanticAnalysis.keyTerms.length - 8} autres termes
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 p-3 bg-white bg-opacity-60 rounded-lg">
        <p className="text-sm text-indigo-700">
          <span className="font-medium">Optimisation appliquée :</span> Votre recherche sera personnalisée selon votre intention, 
          vos mots-clés prioritaires et le niveau de complexité détecté pour vous proposer les sources les plus pertinentes.
        </p>
      </div>
    </div>
  );
};

/**
 * Composant d'affichage des informations de récupération d'URLs
 */
export const RecoveryInfoDisplay = ({ metadata }) => {
  if (!metadata.recovery || metadata.recovery.totalFallbacks <= 0) {
    return null;
  }

  return (
    <div className="recovery-card">
      <h3 className="text-orange-800 font-semibold mb-3 flex items-center section-title">
        <AlertTriangle className="h-6 w-6 mr-2" />
        Récupération intelligente d'URLs
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="stat-box">
          <div className="stat-value text-orange-800">
            {metadata.recovery.totalFallbacks}
          </div>
          <div className="stat-label">Sources récupérées</div>
        </div>
        
        <div className="stat-box">
          <div className="stat-value text-orange-800">
            {metadata.recovery.recoveryRate}%
          </div>
          <div className="stat-label">Taux de récupération</div>
        </div>
        
        <div className="stat-box">
          <div className="stat-value text-orange-800">
            {metadata.recovery.finalAccessibleCount}
          </div>
          <div className="stat-label">Sources finales</div>
        </div>
      </div>
      
      <div className="bg-white bg-opacity-60 rounded-lg p-3">
        <p className="text-sm text-orange-700">
          <span className="font-medium">Optimisation appliquée :</span> {metadata.recovery.totalFallbacks} sources 
          initialement inaccessibles ont été récupérées en utilisant leurs domaines principaux. 
          Vous pouvez accéder aux sites depuis leur page d'accueil.
        </p>
      </div>
      
      {metadata.accessibility && metadata.accessibility.fallbackDetails && 
       metadata.accessibility.fallbackDetails.length > 0 && (
        <details className="mt-3">
          <summary className="text-sm font-medium text-orange-700 cursor-pointer hover:text-orange-800">
            Voir les détails des URLs modifiées ({metadata.accessibility.fallbackDetails.length})
          </summary>
          <div className="mt-2 space-y-2">
            {metadata.accessibility.fallbackDetails.slice(0, 5).map((detail, index) => (
              <div key={index} className="fallback-detail">
                <div className="text-gray-600">
                  <span className="font-medium">Original :</span> 
                  <span className="url-text ml-1">{detail.originalUrl}</span>
                </div>
                <div className="text-green-600">
                  <span className="font-medium">Recommandé :</span> 
                  <span className="url-text ml-1">{detail.fallbackUrl}</span>
                </div>
                {detail.reason && (
                  <div className="text-orange-600 mt-1">
                    <span className="font-medium">Raison :</span> {detail.reason}
                  </div>
                )}
              </div>
            ))}
            {metadata.accessibility.fallbackDetails.length > 5 && (
              <div className="text-xs text-orange-600">
                +{metadata.accessibility.fallbackDetails.length - 5} autres modifications...
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

/**
 * Composant d'en-tête de l'objectif avec boutons d'action
 */
export const ObjectiveHeader = ({ 
  competence, 
  autoConfigApplied, 
  geoAnalysisLoading, 
  preferencesLoaded,
  onSemanticAnalysis,
  onRefresh 
}) => {
  return (
    <div className="mb-6">
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-md overflow-hidden border border-gray-200">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Globe className="h-6 w-6 mr-2 text-blue-600" />
                Recherche 360°
                {autoConfigApplied && (
                  <Chip 
                    label={`Optimisé pour ${autoConfigApplied.country.name}`}
                    size="small"
                    sx={{ 
                      ml: 2,
                      backgroundColor: '#dcfce7', 
                      color: '#166534',
                      fontWeight: 600
                    }}
                  />
                )}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Découvrez des sources pertinentes et fiables pour atteindre votre objectif
                {geoAnalysisLoading && (
                  <span className="ml-2 text-blue-600">
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin inline" />
                    Analyse géographique en cours...
                  </span>
                )}
                {!preferencesLoaded && (
                  <span className="ml-2 text-purple-600">
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin inline" />
                    Chargement des préférences...
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onSemanticAnalysis}
                className="text-gray-600 hover:text-blue-700 flex items-center px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                title="Retour à l'analyse sémantique"
              >
                <Navigation className="h-4 w-4 mr-1" />
                <span className="text-sm">Modifier</span>
              </button>
              
              <button
                onClick={onRefresh}
                className="text-gray-600 hover:text-green-700 flex items-center px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors"
                title="Actualiser les données"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                <span className="text-sm">Actualiser</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="objective-text text-gray-700 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            {competence?.title || "Chargement de l'objectif..."}
          </div>
        </div>
      </div>
    </div>
  );
};